import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Shield, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({
    organization_name: "", contact_name: "", email: "", phone: "",
    website: "", company_size: "", intended_use: "evaluation", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const on = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(await res.text());
      setSubmitted(true);
      toast.success("Request submitted — we'll reach out shortly.");
    } catch (err) {
      toast.error("Unable to submit. Please email hello@resiliencyhub.ai.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 py-16 px-4">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
          <Shield className="w-5 h-5 text-sky-700" />
          <span className="font-semibold">ResiliencyHUB</span>
        </Link>
        <Card data-testid="contact-page">
          <CardHeader>
            <CardTitle className="text-2xl">Request Platform Access</CardTitle>
            <p className="text-sm text-slate-500">
              ResiliencyHUB is granted to qualified enterprises after a brief eligibility review.
              Our BCM team responds within one business day.
            </p>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center py-8 space-y-3" data-testid="contact-submitted">
                <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
                <p className="text-lg font-semibold">Request received</p>
                <p className="text-sm text-slate-600">
                  Our team will reach out via email within one business day. Meanwhile, you can
                  <Link to="/try-demo" className="text-sky-700 hover:underline"> explore the live demo</Link>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Organization Name *</Label>
                    <Input required value={form.organization_name} onChange={on("organization_name")} data-testid="contact-org-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Website</Label>
                    <Input value={form.website} onChange={on("website")} placeholder="www.example.com" data-testid="contact-website" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Your Name *</Label>
                    <Input required value={form.contact_name} onChange={on("contact_name")} data-testid="contact-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Work Email *</Label>
                    <Input required type="email" value={form.email} onChange={on("email")} data-testid="contact-email" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={on("phone")} data-testid="contact-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Company Size</Label>
                    <select value={form.company_size} onChange={on("company_size")} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="contact-size">
                      <option value="">Select…</option>
                      <option>1-50</option><option>51-250</option><option>251-1000</option><option>1000+</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Intended Use</Label>
                  <select value={form.intended_use} onChange={on("intended_use")} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="contact-use">
                    <option value="evaluation">Evaluation / Pilot</option>
                    <option value="production">Production BCM Program</option>
                    <option value="compliance">Audit / Compliance</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Anything else we should know?</Label>
                  <Textarea rows={3} value={form.message} onChange={on("message")} data-testid="contact-message" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full h-11" data-testid="contact-submit">
                  {submitting ? "Submitting…" : "Request Access"}
                </Button>
                <p className="text-xs text-slate-400 text-center inline-flex items-center gap-1.5 w-full justify-center">
                  <Mail className="w-3 h-3" /> Or email <a className="text-sky-700 hover:underline" href="mailto:hello@resiliencyhub.ai">hello@resiliencyhub.ai</a>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
